import { computed, ref } from "vue";
import type { SesionActual } from "@chuy/types";
import { ApiError, api, cuandoSePierdaLaSesion } from "@/api/client";
import { router } from "@/router";

/**
 * Quién anda usando el tablero.
 *
 * Es estado compartido de toda la app —una sola sesión, un solo `ref`— y por eso
 * vive fuera de la función: cada pantalla que lo pida se engancha al mismo, y no
 * hay manera de que dos partes del tablero crean cosas distintas sobre quién
 * eres.
 *
 * En una Mac, donde Chuy corre sin cuentas, esto también funciona: el servidor
 * contesta una sesión de mentiritas con papel de administrador y el tablero se
 * comporta igualito que siempre. Toda la lógica de abajo es la misma en los dos
 * modos, que es justo lo que se quería — no dos tableros, uno.
 */
const sesion = ref<SesionActual | null>(null);
const cargando = ref(true);
/** `true` cuando ya se preguntó al menos una vez, aunque haya sido que no. */
const resuelta = ref(false);

let enganchado = false;

export function useSesion() {
  /**
   * El enganche con la capa HTTP.
   *
   * Cuando el servidor contesta 401 en cualquier llamada —se venció la sesión, la
   * cerraron desde otro dispositivo, suspendieron la cuenta— el tablero se va
   * solo a la pantalla de entrar. Sin esto, lo que ve la persona es cada recuadro
   * de la pantalla poniéndose rojo uno por uno, que es la peor manera de
   * enterarse de que se te acabó la sesión.
   */
  if (!enganchado) {
    enganchado = true;
    cuandoSePierdaLaSesion(() => {
      sesion.value = null;
      if (router.currentRoute.value.name !== "entrar") {
        void router.replace({
          name: "entrar",
          query: { volverA: router.currentRoute.value.fullPath },
        });
      }
    });
  }

  async function cargar(): Promise<SesionActual | null> {
    cargando.value = true;
    try {
      sesion.value = await api.auth.yo();
    } catch (error) {
      // Un 401 aquí es la respuesta correcta, no una falla: quiere decir «no has
      // entrado». Cualquier otra cosa sí es una falla, y se deja pasar hacia
      // arriba para que el tablero pueda decir algo mejor que «entra otra vez».
      if (error instanceof ApiError && error.status === 401) {
        sesion.value = null;
      } else if (error instanceof ApiError && error.status === 0) {
        sesion.value = null;
      } else {
        throw error;
      }
    } finally {
      cargando.value = false;
      resuelta.value = true;
    }
    return sesion.value;
  }

  async function entrar(username: string, password: string): Promise<SesionActual> {
    const actual = await api.auth.entrar({ username, password });
    sesion.value = actual;
    return actual;
  }

  async function salir(): Promise<void> {
    try {
      await api.auth.salir();
    } finally {
      // Aunque el servidor truene: del lado de acá ya te fuiste. Dejar el tablero
      // pintado con los datos de alguien que quiso salirse sería lo peor.
      sesion.value = null;
      await router.replace({ name: "entrar" });
    }
  }

  async function cambiarPassword(actual: string, nueva: string): Promise<void> {
    sesion.value = await api.auth.cambiarPassword({ actual, nueva });
  }

  return {
    sesion,
    cargando,
    resuelta,
    entrado: computed(() => sesion.value !== null),
    esAdmin: computed(() => sesion.value?.role === "ADMIN"),
    /** Trae contraseña temporal: no puede hacer nada más que cambiarla. */
    debeCambiarPassword: computed(() => sesion.value?.mustChangePassword === true),
    cargar,
    entrar,
    salir,
    cambiarPassword,
  };
}
